import { useState } from 'react';
import { specificationsData } from '../../../mocks/specifications';

interface SpecificationDetailsProps {
  selectedModel: string;
}

export default function SpecificationDetails({ selectedModel }: SpecificationDetailsProps) {
  const spec = specificationsData[selectedModel];
  const [expandedSection, setExpandedSection] = useState<string | null>('structure');

  if (!spec) {
    return (
      <div className="bg-gray-900/40 backdrop-blur-xl border border-gray-500/20 rounded-2xl p-12 text-center">
        <i className="ri-error-warning-line text-6xl text-gray-500 mb-4"></i>
        <p className="text-gray-400 text-lg">Model not found</p>
      </div>
    );
  }

  const toggleSection = (section: string) => {
    setExpandedSection(expandedSection === section ? null : section);
  };

  return (
    <div className="space-y-8 w-full">
      {/* Preview Image */}
      <div className="bg-gray-900/40 backdrop-blur-xl border border-gray-500/20 rounded-2xl overflow-hidden w-full">
        <div className="relative w-full h-64 sm:h-80 md:h-96">
          <img 
            src={spec.mainImage} 
            alt={spec.name}
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent"></div>
          <div className="absolute bottom-0 left-0 right-0 p-4 sm:p-6 md:p-8">
            <div className="text-xs sm:text-sm text-cyan-400 mb-2">{spec.series}</div>
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-white mb-2">{spec.name}</h2>
            <p className="text-gray-300 text-sm sm:text-base md:text-lg">{spec.tagline}</p>
          </div>
        </div>
      </div>

      {/* Basic Info Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 sm:gap-4 md:gap-6 w-full">
        <div className="bg-gray-900/40 backdrop-blur-xl border border-gray-500/20 rounded-2xl p-4 sm:p-6 w-full">
          <div className="flex flex-col sm:flex-row items-start sm:items-center gap-2 sm:gap-3">
            <div className="w-10 h-10 sm:w-12 sm:h-12 bg-cyan-500/20 rounded-xl flex items-center justify-center flex-shrink-0">
              <i className="ri-ruler-line text-cyan-400 text-xl sm:text-2xl"></i>
            </div>
            <div className="min-w-0 w-full">
              <div className="text-xs text-gray-400 mb-1">Dimensions</div>
              <div className="text-sm sm:text-base md:text-lg font-semibold text-white break-words">{spec.size}</div>
            </div>
          </div>
        </div>

        <div className="bg-gray-900/40 backdrop-blur-xl border border-gray-500/20 rounded-2xl p-4 sm:p-6 w-full">
          <div className="flex flex-col sm:flex-row items-start sm:items-center gap-2 sm:gap-3">
            <div className="w-10 h-10 sm:w-12 sm:h-12 bg-cyan-500/20 rounded-xl flex items-center justify-center flex-shrink-0">
              <i className="ri-scales-3-line text-cyan-400 text-xl sm:text-2xl"></i>
            </div>
            <div className="min-w-0 w-full">
              <div className="text-xs text-gray-400 mb-1">Weight</div>
              <div className="text-sm sm:text-base md:text-lg font-semibold text-white break-words">{spec.weight}</div>
            </div>
          </div>
        </div>

        <div className="bg-gray-900/40 backdrop-blur-xl border border-gray-500/20 rounded-2xl p-4 sm:p-6 w-full">
          <div className="flex flex-col sm:flex-row items-start sm:items-center gap-2 sm:gap-3">
            <div className="w-10 h-10 sm:w-12 sm:h-12 bg-cyan-500/20 rounded-xl flex items-center justify-center flex-shrink-0">
              <i className="ri-layout-grid-line text-cyan-400 text-xl sm:text-2xl"></i>
            </div>
            <div className="min-w-0 w-full">
              <div className="text-xs text-gray-400 mb-1">Floor Area</div>
              <div className="text-sm sm:text-base md:text-lg font-semibold text-white break-words">{spec.area}</div>
            </div>
          </div>
        </div>

        <div className="bg-gray-900/40 backdrop-blur-xl border border-gray-500/20 rounded-2xl p-4 sm:p-6 w-full">
          <div className="flex flex-col sm:flex-row items-start sm:items-center gap-2 sm:gap-3">
            <div className="w-10 h-10 sm:w-12 sm:h-12 bg-cyan-500/20 rounded-xl flex items-center justify-center flex-shrink-0">
              <i className="ri-group-line text-cyan-400 text-xl sm:text-2xl"></i>
            </div>
            <div className="min-w-0 w-full">
              <div className="text-xs text-gray-400 mb-1">Capacity</div>
              <div className="text-sm sm:text-base md:text-lg font-semibold text-white break-words">{spec.capacity}</div>
            </div>
          </div>
        </div>
      </div>

      {/* Key Features */}
      <div className="bg-gray-900/40 backdrop-blur-xl border border-gray-500/20 rounded-2xl p-4 sm:p-6 md:p-8 w-full">
        <h3 className="text-xl sm:text-2xl font-bold text-white mb-4 sm:mb-6 flex items-center gap-3">
          <i className="ri-star-line text-cyan-400"></i>
          Key Features
        </h3>
        <div className="grid sm:grid-cols-2 gap-3 sm:gap-4 w-full">
          {spec.keyFeatures.map((feature, index) => (
            <div key={index} className="flex items-start gap-3 p-3 sm:p-4 bg-gray-800/40 rounded-xl w-full">
              <i className="ri-checkbox-circle-fill text-cyan-400 text-lg sm:text-xl flex-shrink-0 mt-0.5"></i>
              <span className="text-sm sm:text-base text-gray-300 break-words">{feature}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Technical Specifications - Accordion Style */}
      <div className="bg-gray-900/40 backdrop-blur-xl border border-gray-500/20 rounded-2xl p-4 sm:p-6 md:p-8 w-full">
        <h3 className="text-xl sm:text-2xl font-bold text-white mb-4 sm:mb-6 flex items-center gap-3">
          <i className="ri-settings-3-line text-cyan-400"></i>
          Technical Specifications
        </h3>
        <div className="space-y-4 w-full">
          {/* Structure Section */}
          <div className="border border-gray-500/20 rounded-xl overflow-hidden w-full">
            <button
              onClick={() => toggleSection('structure')}
              className="w-full flex items-center justify-between p-4 sm:p-6 bg-gray-800/40 hover:bg-gray-800/60 transition-all cursor-pointer"
            >
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 sm:w-10 sm:h-10 bg-cyan-500/20 rounded-lg flex items-center justify-center flex-shrink-0">
                  <i className="ri-building-line text-cyan-400 text-lg sm:text-xl"></i>
                </div>
                <span className="text-base sm:text-lg font-semibold text-white">Structure</span>
              </div>
              <i className={`ri-arrow-down-s-line text-xl sm:text-2xl text-cyan-400 transition-transform duration-300 flex-shrink-0 ${expandedSection === 'structure' ? 'rotate-180' : ''}`}></i>
            </button>
            {expandedSection === 'structure' && (
              <div className="p-4 sm:p-6 bg-gray-900/40 border-t border-gray-500/20 w-full">
                <div className="grid sm:grid-cols-2 gap-3 sm:gap-4 w-full">
                  {Object.entries(spec.technicalSpecs.structure).map(([key, value]) => (
                    <div key={key} className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-2 p-3 sm:p-4 bg-gray-800/40 rounded-xl w-full">
                      <span className="text-sm sm:text-base text-gray-400 capitalize break-words">
                        {key.replace(/([A-Z])/g, ' $1').trim()}
                      </span>
                      <span className="text-sm sm:text-base text-white font-medium break-words">{String(value)}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Glass System Section */}
          <div className="border border-gray-500/20 rounded-xl overflow-hidden w-full">
            <button
              onClick={() => toggleSection('glassSystem')}
              className="w-full flex items-center justify-between p-4 sm:p-6 bg-gray-800/40 hover:bg-gray-800/60 transition-all cursor-pointer"
            >
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 sm:w-10 sm:h-10 bg-cyan-500/20 rounded-lg flex items-center justify-center flex-shrink-0">
                  <i className="ri-window-line text-cyan-400 text-lg sm:text-xl"></i>
                </div>
                <span className="text-base sm:text-lg font-semibold text-white">Glass System</span>
              </div>
              <i className={`ri-arrow-down-s-line text-xl sm:text-2xl text-cyan-400 transition-transform duration-300 flex-shrink-0 ${expandedSection === 'glassSystem' ? 'rotate-180' : ''}`}></i>
            </button>
            {expandedSection === 'glassSystem' && (
              <div className="p-4 sm:p-6 bg-gray-900/40 border-t border-gray-500/20 w-full">
                <div className="grid sm:grid-cols-2 gap-3 sm:gap-4 w-full">
                  {Object.entries(spec.technicalSpecs.glassSystem).map(([key, value]) => (
                    <div key={key} className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-2 p-3 sm:p-4 bg-gray-800/40 rounded-xl w-full">
                      <span className="text-sm sm:text-base text-gray-400 capitalize break-words">
                        {key.replace(/([A-Z])/g, ' $1').trim()}
                      </span>
                      <span className="text-sm sm:text-base text-white font-medium break-words">{String(value)}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Smart Systems Section */}
          <div className="border border-gray-500/20 rounded-xl overflow-hidden w-full">
            <button
              onClick={() => toggleSection('smartSystems')}
              className="w-full flex items-center justify-between p-4 sm:p-6 bg-gray-800/40 hover:bg-gray-800/60 transition-all cursor-pointer"
            >
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 sm:w-10 sm:h-10 bg-cyan-500/20 rounded-lg flex items-center justify-center flex-shrink-0">
                  <i className="ri-cpu-line text-cyan-400 text-lg sm:text-xl"></i>
                </div>
                <span className="text-base sm:text-lg font-semibold text-white">Smart Systems</span>
              </div>
              <i className={`ri-arrow-down-s-line text-xl sm:text-2xl text-cyan-400 transition-transform duration-300 flex-shrink-0 ${expandedSection === 'smartSystems' ? 'rotate-180' : ''}`}></i>
            </button>
            {expandedSection === 'smartSystems' && (
              <div className="p-4 sm:p-6 bg-gray-900/40 border-t border-gray-500/20 w-full">
                <div className="space-y-3 sm:space-y-4 w-full">
                  {Object.entries(spec.technicalSpecs.smartSystems).map(([key, value]) => (
                    <div key={key} className="flex items-start gap-3 p-3 sm:p-4 bg-gray-800/40 rounded-xl w-full">
                      <i className="ri-checkbox-circle-fill text-cyan-400 text-lg sm:text-xl flex-shrink-0 mt-0.5"></i>
                      <div className="min-w-0 w-full">
                        <div className="text-sm sm:text-base text-white font-medium mb-1 capitalize break-words">
                          {key.replace(/([A-Z])/g, ' $1').trim()}
                        </div>
                        <div className="text-xs sm:text-sm text-gray-400 break-words">{String(value)}</div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Materials & Construction */}
      <div className="bg-gray-900/40 backdrop-blur-xl border border-gray-500/20 rounded-2xl p-4 sm:p-6 md:p-8 w-full">
        <h3 className="text-xl sm:text-2xl font-bold text-white mb-4 sm:mb-6 flex items-center gap-3">
          <i className="ri-hammer-line text-cyan-400"></i>
          Materials & Construction
        </h3>
        <div className="grid sm:grid-cols-2 gap-4 sm:gap-6 w-full">
          {spec.materials.map((material, index) => (
            <div key={index} className="p-4 sm:p-6 bg-gray-800/40 rounded-xl w-full">
              <div className="flex items-center gap-3 mb-3">
                <div className="w-8 h-8 sm:w-10 sm:h-10 bg-cyan-500/20 rounded-lg flex items-center justify-center flex-shrink-0">
                  <i className={`${material.icon} text-cyan-400 text-lg sm:text-xl`}></i>
                </div>
                <h4 className="text-base sm:text-lg font-semibold text-white break-words">{material.name}</h4>
              </div>
              <p className="text-xs sm:text-sm text-gray-300 break-words">{material.description}</p>
            </div>
          ))}
        </div>
      </div>

      {/* Installation & Delivery */}
      <div className="bg-gray-900/40 backdrop-blur-xl border border-gray-500/20 rounded-2xl p-4 sm:p-6 md:p-8 w-full">
        <h3 className="text-xl sm:text-2xl font-bold text-white mb-4 sm:mb-6 flex items-center gap-3">
          <i className="ri-truck-line text-cyan-400"></i>
          Installation & Delivery
        </h3>
        <div className="grid sm:grid-cols-2 gap-4 sm:gap-6 w-full">
          <div className="space-y-4 w-full">
            <div className="flex items-start gap-3">
              <i className="ri-time-line text-cyan-400 text-lg sm:text-xl flex-shrink-0 mt-1"></i>
              <div className="min-w-0 w-full">
                <div className="text-sm sm:text-base text-white font-medium mb-1">Installation Time</div>
                <div className="text-xs sm:text-sm text-gray-400 break-words">{spec.installation.time}</div>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <i className="ri-team-line text-cyan-400 text-lg sm:text-xl flex-shrink-0 mt-1"></i>
              <div className="min-w-0 w-full">
                <div className="text-sm sm:text-base text-white font-medium mb-1">Team Required</div>
                <div className="text-xs sm:text-sm text-gray-400 break-words">{spec.installation.team}</div>
              </div>
            </div>
          </div>
          <div className="space-y-4 w-full">
            <div className="flex items-start gap-3">
              <i className="ri-tools-line text-cyan-400 text-lg sm:text-xl flex-shrink-0 mt-1"></i>
              <div className="min-w-0 w-full">
                <div className="text-sm sm:text-base text-white font-medium mb-1">Foundation</div>
                <div className="text-xs sm:text-sm text-gray-400 break-words">{spec.installation.foundation}</div>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <i className="ri-ship-line text-cyan-400 text-lg sm:text-xl flex-shrink-0 mt-1"></i>
              <div className="min-w-0 w-full">
                <div className="text-sm sm:text-base text-white font-medium mb-1">Delivery Method</div>
                <div className="text-xs sm:text-sm text-gray-400 break-words">{spec.installation.delivery}</div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* CTA Section */}
      <div className="bg-gradient-to-r from-cyan-500/20 to-cyan-600/20 backdrop-blur-xl border border-cyan-400/30 rounded-2xl p-6 sm:p-8 text-center w-full">
        <h3 className="text-xl sm:text-2xl font-bold text-white mb-3 sm:mb-4">Interested in {spec.name}?</h3>
        <p className="text-sm sm:text-base text-gray-300 mb-4 sm:mb-6 max-w-2xl mx-auto px-4">
          Get a custom quote or schedule a consultation with our team to discuss your specific requirements
        </p>
        <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 justify-center px-4">
          <a 
            href="/contact"
            className="w-full sm:w-auto px-6 sm:px-8 py-3 bg-cyan-500 text-black text-sm sm:text-base font-medium rounded-full hover:bg-cyan-400 transition-all hover:shadow-[0_0_20px_rgba(0,217,255,0.5)] whitespace-nowrap cursor-pointer"
          >
            Request Quote
          </a>
          <a 
            href="/contact"
            className="w-full sm:w-auto px-6 sm:px-8 py-3 bg-gray-800/60 border border-gray-500/30 text-white text-sm sm:text-base font-medium rounded-full hover:bg-gray-700/60 hover:border-gray-400/50 transition-all whitespace-nowrap cursor-pointer"
          >
            Schedule Consultation
          </a>
        </div>
      </div>
    </div>
  );
}